/**
 * ProficiencyWatchDigest (v6.36.25) — Compact card showing the top 3 high-
 * relevance WIDA/NY/NYC news items. Designed to drop into the teacher
 * dashboard so educators stay aware of policy/program updates that
 * may impact their classroom.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Newspaper, ExternalLink, TrendingUp, TrendingDown, Sparkles, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPACT_BG = {
  strength: { bg: 'bg-green-50', text: 'text-green-700', icon: TrendingUp },
  weakness: { bg: 'bg-red-50', text: 'text-red-700', icon: TrendingDown },
  opportunity: { bg: 'bg-blue-50', text: 'text-blue-700', icon: Sparkles },
  neutral: { bg: 'bg-gray-50', text: 'text-gray-700', icon: AlertTriangle },
};

const ProficiencyWatchDigest = ({ canSeeAdmin = false }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/proficiency-watch/teacher-digest`);
        if (alive) {
          setItems((res.data?.items || []).slice(0, 3));
        }
      } catch (_e) {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <div className="border-2 border-[#002FA7] p-5" data-testid="proficiency-watch-digest">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Newspaper size={18} className="text-[#002FA7]" />
          <h3 className="font-bold text-sm uppercase tracking-wider">What's New in WIDA · NY · NYC</h3>
        </div>
        {canSeeAdmin && (
          <Link to="/proficiency-watch" className="text-xs font-semibold text-[#002FA7] hover:underline">
            See all →
          </Link>
        )}
      </div>
      <ul className="space-y-3" data-testid="proficiency-watch-digest-list">
        {items.map((it) => {
          const cfg = IMPACT_BG[it.mosaic_impact] || IMPACT_BG.neutral;
          const Icon = cfg.icon;
          return (
            <li key={it.id} className="flex items-start gap-3" data-testid={`wida-digest-item-${it.id}`}>
              <div className={`${cfg.bg} ${cfg.text} px-2 py-1 text-xs font-bold uppercase flex items-center gap-1 flex-shrink-0`}>
                <Icon size={11} /> {it.relevance_score}
              </div>
              <div className="flex-1 min-w-0">
                <a
                  href={it.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-gray-900 hover:text-[#002FA7] inline-flex items-start gap-1"
                >
                  {it.title} <ExternalLink size={10} className="mt-1 flex-shrink-0" />
                </a>
                {it.summary && (
                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{it.summary}</p>
                )}
                <div className="text-xs text-gray-400 mt-0.5">{it.source}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ProficiencyWatchDigest;
